import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { creditosPendientes } from '../utils/creditos';
import { cuentaDeFinca, cuentaDeCliente, neto } from '../utils/deuda';

// La primera mirada del día.
//
// El rendimiento va primero y en grande, aunque no sea una cifra de plata: es
// la que decide si el negocio gana, y la única que nadie mira porque no aparece
// en ningún cuaderno.
function Panel({ irA }) {
  const [datos, setDatos] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [error, setError] = useState('');
  // El buscador. Va en el Panel y no en una pestaña porque no se usa cuando uno
  // decide usarlo: se usa cuando suena el teléfono, y en ese momento la
  // pantalla que está abierta es esta.
  const [busca, setBusca] = useState('');
  const [halla, setHalla] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [falloBusca, setFalloBusca] = useState(false);

  useEffect(() => {
    let vivo = true;
    axios.get('/api/panel')
      .then(r => { if (vivo) setDatos(r.data); })
      .catch(() => { if (vivo) setError('No se pudo cargar el panel'); });
    axios.get('/api/inventario')
      .then(r => { if (vivo) setLotes(r.data || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Se espera a que deje de escribir. Sin esta pausa, «Rodríguez» son diez
  // consultas y la última en llegar no es siempre la de lo último que escribió:
  // el nombre queda mostrando los resultados de otra búsqueda.
  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) { setHalla(null); setBuscando(false); setFalloBusca(false); return; }
    setBuscando(true);
    let vivo = true;
    const t = setTimeout(() => {
      axios.get('/api/buscar', { params: { q } })
        .then(r => { if (vivo) { setHalla(r.data); setFalloBusca(false); } })
        .catch(() => { if (vivo) setFalloBusca(true); })
        .finally(() => { if (vivo) setBuscando(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!datos) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const { leche, produccion, ventas, porCobrar, porPagar, prestado, ganado, gastos } = datos;

  // Lo que hay en cámara y lo que se está por vencer. El queso no espera: un
  // lote que se pasa se pierde entero, así que va en la primera pantalla.
  const enCamara = lotes.reduce((s, l) => s + l.kilos, 0);
  const dias = (v) => v
    ? Math.floor((new Date(v + 'T00:00:00') - new Date(hoy() + 'T00:00:00')) / 86400000)
    : null;
  const porVencer = lotes.filter(l => { const d = dias(l.vence); return d !== null && d <= 5; })
    .sort((a, b) => String(a.vence).localeCompare(String(b.vence)));
  const kilosPorVencer = porVencer.reduce((s, l) => s + l.kilos, 0);

  const rendimiento = produccion.kilos > 0 ? produccion.litros / produccion.kilos : null;
  const costoKilo = produccion.kilos > 0 ? produccion.costo / produccion.kilos : null;

  const Dato = ({ rotulo, valor, pie, color }) => (
    <div className="dato" style={{ '--color': color }}>
      <div className="rotulo">{rotulo}</div>
      <div className="valor cifra">{valor}</div>
      {pie && <div className="pie">{pie}</div>}
    </div>
  );

  const sinProducir = produccion.cuajadas === 0;

  // Los despachos fiados con los abonos ya repartidos, igual que en Clientes.
  const despachos = halla
    ? creditosPendientes(halla.ventas, halla.abonos) : [];

  // Se junta a la MISMA persona aunque el programa la tenga en dos tablas.
  //
  // Se juntan por cédula cuando la hay, y si no por nombre. La cédula es lo
  // único que de verdad identifica; el nombre se escribe distinto cada vez
  // —«Lenin Ortiz» y «LENIN ORTIZ G.»— y por eso es el último recurso, no el
  // primero. Cuando no junta bien, salen dos tarjetas: molesta, pero no miente.
  const encontrados = [];
  if (halla) {
    const porClave = new Map();
    const meter = (clave, nombre, senas, lado, fila) => {
      if (!porClave.has(clave)) {
        porClave.set(clave, { clave, nombre, senas, fincas: [], clientes: [] });
      }
      porClave.get(clave)[lado].push(fila);
    };
    for (const f of halla.fincas || []) {
      const doc = String(f.documento || '').trim();
      meter(doc ? 'd:' + doc : 'n:' + String(f.propietario || f.nombre).trim().toLowerCase(),
        f.propietario || f.nombre,
        [doc && 'CC ' + doc, f.telefono, f.ubicacion].filter(Boolean).join(' · '),
        'fincas', f);
    }
    for (const c of halla.clientes || []) {
      const doc = String(c.documento || '').trim();
      meter(doc ? 'd:' + doc : 'n:' + String(c.nombre).trim().toLowerCase(),
        c.nombre,
        [doc && 'CC ' + doc, c.telefono, c.ciudad].filter(Boolean).join(' · '),
        'clientes', c);
    }
    for (const p of porClave.values()) {
      encontrados.push({ ...p, cuenta: neto(p) });
    }
  }

  return (
    <>
      <div className="encabezado">
        <h1>Panel</h1>
        <p>Los últimos 30 días</p>
      </div>

      {/* EL BUSCADOR: «¿cuánto te debo?»
          El programa tiene a la misma persona partida en dos —la finca que le
          vende la leche y el cliente que le compra el queso— y quien llama no
          sabe eso. Preguntando por un nombre hay que mirar en los dos lados.

          Busca también por cédula y por teléfono: cuando llaman, lo que se
          tiene a la mano es el número que aparece en la pantalla. */}
      <div className="bloque">
        <h2>🔎 ¿Cuánto le debe?</h2>
        <div className="cuerpo">
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Nombre, cédula o teléfono"
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px',
                     fontSize: 16, borderRadius: 8, border: '1px solid var(--linea)' }} />

          {falloBusca && (
            <div className="aviso malo" style={{ marginTop: 10 }}>
              No se pudo buscar. Recargue la página; si sigue igual, avise.
            </div>
          )}

          {buscando && (
            <p style={{ color: 'var(--tinta-tenue)', fontSize: 13, margin: '10px 0 0' }}>
              Buscando…
            </p>
          )}

          {halla && !buscando && encontrados.length === 0 && (
            <p style={{ color: 'var(--tinta-tenue)', fontSize: 14, margin: '10px 0 0' }}>
              No aparece nadie con eso. Puede estar escrito distinto, o la ficha
              no tener la cédula guardada.
            </p>
          )}

          {!buscando && encontrados.map((p) => (
            <div key={p.clave} style={{ border: '1px solid var(--linea)', borderRadius: 10,
                                        padding: 14, marginTop: 12 }}>
              <div className="recio" style={{ fontSize: 17 }}>{p.nombre}</div>
              {p.senas && (
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', marginTop: 2 }}>
                  {p.senas}
                </div>
              )}

              {/* LA RESPUESTA, arriba y en una frase. Lo de abajo es de dónde
                  sale; esto es lo que se contesta por teléfono. */}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--linea)' }}>
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                  {p.cuenta.saldo > 0 ? 'Esta persona le debe a usted'
                    : p.cuenta.saldo < 0 ? 'Usted le debe a esta persona'
                    : 'Están a mano'}
                </div>
                <div className="cifra recio" style={{ fontSize: 26,
                  color: p.cuenta.saldo > 0 ? 'var(--bueno)'
                       : p.cuenta.saldo < 0 ? 'var(--malo)' : 'var(--tinta-tenue)' }}>
                  {plata(Math.abs(p.cuenta.saldo))}
                </div>
              </div>

              {/* De dónde sale. Un total sin desglose no se puede defender en
                  una llamada: el otro pregunta «¿de qué?» y no hay respuesta. */}
              <div style={{ marginTop: 10, fontSize: 14 }}>
                {p.fincas.map(f => {
                  const c = cuentaDeFinca(f);
                  return (
                    <div key={'f' + f.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                        🚜 {f.nombre} — leche recibida
                      </div>
                      <div>
                        Usted le debe <strong>{plata(c.leche)}</strong>
                        {c.hayDescuento && (
                          <>
                            {' '}menos <strong>{plata(c.prestado)}</strong> que le prestó
                            {' '}→ <strong>a pagar {plata(c.pagar)}</strong>
                            {c.quedaDebiendo > 0 && (
                              <div style={{ color: 'var(--ojo)', fontSize: 13 }}>
                                No alcanza a cubrirlo: le quedan debiendo{' '}
                                {plata(c.quedaDebiendo)} del préstamo.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {p.clientes.map(cl => {
                  const c = cuentaDeCliente(cl, despachos);
                  if (c.debe <= 0 && c.aFavor <= 0) return (
                    <div key={'c' + cl.id} style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                      🧀 Le compra queso y está al día.
                    </div>
                  );
                  return (
                    <div key={'c' + cl.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                        🧀 Queso fiado
                      </div>
                      {c.debe > 0 ? (
                        <>
                          <div>Le debe <strong>{plata(c.debe)}</strong></div>
                          {c.despachos.map(d => (
                            <div key={d.id} style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                              · despacho del {fecha(d.fecha)}: {plata(d.saldo)}
                              {d.parcial && ` (ya abonó ${plata(d.abonado)})`}
                            </div>
                          ))}
                        </>
                      ) : (
                        <div>Tiene <strong>{plata(c.aFavor)}</strong> a favor.</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {p.fincas.length > 0 && (
                  <button className="boton chico suave" onClick={() => irA('fincas')}>
                    Ir a Fincas
                  </button>
                )}
                {p.clientes.length > 0 && (
                  <button className="boton chico suave" onClick={() => irA('cartera')}>
                    Ir a Clientes
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {sinProducir && (
        <div className="aviso ojo">
          <strong>Todavía no hay cuajadas registradas.</strong> El rendimiento y el
          costo del kilo aparecen aquí en cuanto se cierre la primera.{' '}
          <button className="boton chico" style={{ marginLeft: 8 }}
                  onClick={() => irA('produccion')}>
            Registrar una cuajada
          </button>
        </div>
      )}

      {porVencer.length > 0 && (
        <div className="aviso malo">
          <strong>
            {peso(Math.round(kilosPorVencer * 100) / 100)} se vencen pronto
          </strong>{' '}
          — {porVencer.slice(0, 3).map(l => {
            const d = dias(l.vence);
            return `${l.producto} lote ${l.lote} (${d < 0 ? 'vencido' : d === 0 ? 'hoy' : `${d} d`})`;
          }).join(', ')}
          {porVencer.length > 3 && ` y ${porVencer.length - 3} más`}.
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => irA('inventario')}>
            Ver cuáles
          </button>
        </div>
      )}

      <div className="rejilla">
        <Dato
          rotulo="❄️ En cámara"
          valor={lotes.length > 0 ? peso(Math.round(enCamara * 100) / 100) : '—'}
          pie={lotes.length > 0
            ? `${lotes.length} ${lotes.length === 1 ? 'lote' : 'lotes'}`
            : 'sin queso todavía'}
          color="var(--queso)"
        />
        <Dato
          rotulo="🧀 Rendimiento"
          valor={rendimiento ? rendimiento.toFixed(2) + ' L/kg' : '—'}
          pie={rendimiento ? 'litros de leche por kilo de queso' : 'sin producción todavía'}
          color="var(--queso)"
        />
        <Dato
          rotulo="💰 Costo del kilo"
          valor={costoKilo ? plata(costoKilo) : '—'}
          pie={costoKilo ? 'leche, insumos y mano de obra' : 'sin producción todavía'}
          color="var(--queso)"
        />
        <Dato
          rotulo="🥛 Leche comprada"
          valor={peso(leche.litros, 'L')}
          pie={plata(leche.costo)}
          color="var(--leche)"
        />
        <Dato
          rotulo="🧀 Queso producido"
          valor={peso(produccion.kilos, 'kg')}
          pie={`${produccion.cuajadas} ${produccion.cuajadas === 1 ? 'cuajada' : 'cuajadas'}`}
          color="var(--queso)"
        />
      </div>

      <div className="rejilla">
        <Dato
          rotulo="🛒 Vendido"
          valor={plata(ventas.total)}
          pie={`${ventas.cuantas} ${ventas.cuantas === 1 ? 'venta' : 'ventas'}`}
          color="var(--venta)"
        />
        <Dato
          rotulo="📥 Le deben"
          valor={plata(porCobrar.saldo)}
          pie="clientes a crédito"
          color="var(--venta)"
        />
        <Dato
          rotulo="📤 Debe a las fincas"
          valor={plata(porPagar.saldo)}
          pie="leche recibida sin pagar"
          color="var(--leche)"
        />
        {/* Lo prestado a los dueños de finca. Solo aparece si hay algo: una
            casilla en cero todos los días se vuelve parte del fondo, y el día
            que tenga plata afuera no se va a notar. */}
        {prestado && prestado.saldo > 0 && (
          <Dato
            rotulo="💵 Le prestó a las fincas"
            valor={plata(prestado.saldo)}
            pie={`${prestado.cuantos} ${prestado.cuantos === 1 ? 'préstamo' : 'préstamos'}` +
                 ` a ${prestado.fincas} ${prestado.fincas === 1 ? 'finca' : 'fincas'}` +
                 ' · se descuenta al pagar'}
            color="var(--ojo)"
          />
        )}

        <Dato
          rotulo="🐄 Ganado"
          valor={`${ganado.activas}`}
          pie={ganado.activas === 1 ? 'cabeza activa' : 'cabezas activas'}
          color="var(--ganado)"
        />
      </div>

      {/* LA PLATA QUE ESTÁ AFUERA.
          Las tres cifras existían, pero repartidas en tres pantallas: lo que le
          deben en Clientes, lo que debe en Fincas, y lo prestado había que
          buscarlo finca por finca. La pregunta que se hace un negocio no es
          ninguna de las tres por separado — es «¿cuánta plata mía está en la
          calle?», y esa no se contestaba en ningún lado. */}
      {(porCobrar.saldo > 0 || porPagar.saldo > 0 || (prestado && prestado.saldo > 0)) && (
        <div className="bloque">
          <h2>💸 La plata que está afuera</h2>
          <div className="cuerpo">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 480 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Le deben los clientes <span style={{ color: 'var(--tinta-tenue)' }}>
                  · queso fiado</span></span>
                <span className="cifra recio" style={{ color: 'var(--bueno)' }}>
                  +{plata(porCobrar.saldo)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Le prestó a las fincas <span style={{ color: 'var(--tinta-tenue)' }}>
                  · se descuenta al pagarles</span></span>
                <span className="cifra recio" style={{ color: 'var(--bueno)' }}>
                  +{plata(prestado ? prestado.saldo : 0)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Usted les debe a las fincas <span style={{ color: 'var(--tinta-tenue)' }}>
                  · leche recibida</span></span>
                <span className="cifra recio" style={{ color: 'var(--malo)' }}>
                  −{plata(porPagar.saldo)}
                </span>
              </div>
              {/* El neto va al final y con su nombre. Los tres números sueltos
                  no contestan la pregunta; la resta sí. */}
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            borderTop: '1px solid var(--linea)', paddingTop: 6,
                            fontWeight: 700, fontSize: 17 }}>
                <span>{(() => {
                  const n = porCobrar.saldo + (prestado ? prestado.saldo : 0) - porPagar.saldo;
                  return n >= 0 ? 'A su favor' : 'En contra';
                })()}</span>
                <span className="cifra">{(() => {
                  const n = porCobrar.saldo + (prestado ? prestado.saldo : 0) - porPagar.saldo;
                  return plata(Math.abs(n));
                })()}</span>
              </div>
            </div>
            <div className="aviso" style={{ marginTop: 12, marginBottom: 0 }}>
              Lo prestado a las fincas cuenta a su favor porque se lo descuenta
              cuando les pague la leche. No es plata perdida: es plata adelantada.
            </div>
          </div>
        </div>
      )}

      {/* La cuenta escrita, no solo los números sueltos. Quien la lee una vez
          entiende para qué sirve el resto del programa. */}
      {rendimiento && (
        <div className="bloque">
          <h2>🧮 De dónde sale el costo del kilo</h2>
          <div className="cuerpo">
            <div className="tabla-caja">
              <table className="datos">
                <tbody>
                  <tr>
                    <td>Leche comprada en el periodo</td>
                    <td className="num">{peso(leche.litros, 'L')}</td>
                    <td className="num recio">{plata(leche.costo)}</td>
                  </tr>
                  <tr>
                    <td>Leche que entró a las cuajadas</td>
                    <td className="num">{peso(produccion.litros, 'L')}</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td>Queso que salió</td>
                    <td className="num">{peso(produccion.kilos, 'kg')}</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td className="recio">Rendimiento</td>
                    <td className="num recio">{rendimiento.toFixed(2)} L/kg</td>
                    <td className="num recio">{plata(costoKilo)} por kilo</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ color: 'var(--tinta-tenue)', fontSize: 13, margin: '14px 0 0' }}>
              Si el rendimiento sube, cada kilo cuesta más aunque la leche valga lo
              mismo. Es lo que hay que vigilar todos los días.
            </p>
          </div>
        </div>
      )}

      {gastos.total > 0 && (
        <div className="rejilla">
          <Dato rotulo="💸 Gastos del periodo" valor={plata(gastos.total)} color="var(--malo)" />
        </div>
      )}
    </>
  );
}

export default Panel;
